import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSSE } from './useSSE'
import { queryKeys } from '@/lib/queryKeys'
import {
  useThumbnailTrackingStore,
  type ThumbnailTrackingEntry,
} from '@/stores/useThumbnailTrackingStore'

export function useThumbnailSSE(entry: ThumbnailTrackingEntry) {
  const queryClient = useQueryClient()
  const removeEntry = useThumbnailTrackingStore(s => s.removeEntry)

  const invalidateDagRows = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.dagTable.rowsPrefix(entry.planId, entry.versionId),
    })
    queryClient.invalidateQueries({
      queryKey: queryKeys.lineItemImages.byLine(
        entry.planId,
        entry.versionId,
        entry.lineId
      ),
    })
  }, [queryClient, entry.planId, entry.versionId, entry.lineId])

  const handleComplete = useCallback(() => {
    invalidateDagRows()
    removeEntry(entry.lineId)
  }, [invalidateDagRows, removeEntry, entry.lineId])

  const handleError = useCallback(() => {
    invalidateDagRows()
    // Fallback: retry invalidation after 10s in case thumbnail completed but SSE failed
    setTimeout(() => {
      invalidateDagRows()
      removeEntry(entry.lineId)
    }, 10_000)
  }, [invalidateDagRows, removeEntry, entry.lineId])

  useSSE({
    url: entry.eventsUrl,
    onComplete: handleComplete,
    onError: handleError,
    enabled: true,
  })
}
